import React, {useContext, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import {InvoiceService, InvoiceEmitter} from '../../../../../services/invoiceService';
import EmitterDialog from './EmitterDialog';

const EmitterTab: React.FC = () => {
    const {call_backend_api} = useContext(APIWrapperContext);
    const [emitters, setEmitters] = useState<InvoiceEmitter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editEmitter, setEditEmitter] = useState<InvoiceEmitter | null>(null);

    useEffect(() => {
        loadEmitters();
    }, []);

    const loadEmitters = () => {
        setLoading(true);
        setError(null);
        call_backend_api(
            InvoiceService.getEmitters(false), // Include inactive
            (data: InvoiceEmitter[]) => {
                setEmitters(data);
                setLoading(false);
            },
            (err: any) => {
                console.error('Error loading emitters:', err);
                setError('Fehler beim Laden der Rechnungsaussteller');
                setLoading(false);
            },
            undefined,
            false
        );
    };

    const handleAdd = () => {
        setEditEmitter(null);
        setDialogOpen(true);
    };

    const handleEdit = (emitter: InvoiceEmitter) => {
        setEditEmitter(emitter);
        setDialogOpen(true);
    };

    const handleDelete = (emitter: InvoiceEmitter) => {
        if (!window.confirm(`Möchten Sie "${emitter.name}" wirklich deaktivieren?`)) {
            return;
        }
        call_backend_api(
            InvoiceService.deleteEmitter(emitter.id),
            () => {
                loadEmitters();
            },
            (err: any) => {
                console.error('Error deleting emitter:', err);
                setError('Fehler beim Deaktivieren des Rechnungsausstellers');
            },
            undefined,
            false
        );
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditEmitter(null);
    };

    const handleDialogSave = () => {
        setDialogOpen(false);
        setEditEmitter(null);
        loadEmitters();
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                <Typography variant="h6">
                    Rechnungsaussteller verwalten
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Neu
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {emitters.length === 0 ? (
                <Box sx={{py: 4, textAlign: 'center'}}>
                    <Typography variant="body1" color="text.secondary">
                        Noch keine Rechnungsaussteller vorhanden.
                    </Typography>
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kürzel</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Stadt</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {emitters.map((emitter) => (
                                <TableRow key={emitter.id} hover>
                                    <TableCell>{emitter.short_key}</TableCell>
                                    <TableCell>{emitter.name}</TableCell>
                                    <TableCell>{emitter.city}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={emitter.active ? 'Aktiv' : 'Inaktiv'}
                                            color={emitter.active ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Bearbeiten">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(emitter)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Deaktivieren">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(emitter)}
                                                disabled={!emitter.active}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <EmitterDialog
                open={dialogOpen}
                emitter={editEmitter}
                onClose={handleDialogClose}
                onSave={handleDialogSave}
            />
        </Box>
    );
};

export default EmitterTab;
